import {
  CancellationReason,
  ChatMessage,
  ClientFeeScheduleDetail,
  Driver,
  OrganizationPaymentMethod,
  ProviderFeeScheduleDetail,
  ReservationDetail,
  ReservationStatus,
  RideRequestDetail,
  TransitProgramInfo,
  Vehicle,
  Provider,
  ServiceLevel
} from '@uzurv/clara';
import { DateFormatConstants, isNullOrUndefined, LocalStorageConstants, RideRequestTypeConstants, TimeZoneUtil } from '@uzurv/core';
import { GenericListIconDefinition } from '../generic-list/generic-list-icon';

export class ReservationListItemVm {
  static RESERVATION_LIST_ITEM_STATUS_SCHEDULED = 'Scheduled';
  static RESERVATION_LIST_ITEM_STATUS_SELECT_DRIVER = 'Select Driver';
  static RESERVATION_LIST_ITEM_STATUS_PENDING = 'Pending';
  static RESERVATION_LIST_ITEM_STATUS_WILL_CALL = 'Will Call';
  static RESERVATION_LIST_ITEM_STATUS_RECOVERY = 'Recovery';
  static RESERVATION_LIST_ITEM_STATUS_RIDE_NOW = 'Ride Now';
  static RESERVATION_LIST_ITEM_STATUS_EN_ROUTE = 'Driver en route';
  static RESERVATION_LIST_ITEM_STATUS_PICK_UP_ARRIVED = 'Pick-up arrived';
  static RESERVATION_LIST_ITEM_STATUS_IN_PROGRESS = 'In progress';
  static RESERVATION_LIST_ITEM_STATUS_DROP_OFF_ARRIVED = 'Drop-off arrived';
  static RESERVATION_LIST_ITEM_STATUS_COMPLETED = 'Completed';
  static RESERVATION_LIST_ITEM_STATUS_CANCELLED = 'Cancelled';

  id: number;
  driver: Driver;
  provider: Provider;
  vehicle: Vehicle;
  vehicleId: number;
  completedDate: Date;
  cancelled: boolean;
  cancelledDate: Date;
  cancellationReason: CancellationReason;
  reservationStatus: ReservationStatus;
  rideRequestDetail: RideRequestDetail;
  createdDate: Date;
  reservedDate: Date;
  modifiedDate: Date;
  transitProgramCustomerId: string;
  createdBy: string;
  reservedBy: string;
  driverEnRouteDate: Date;
  pickUpArrivedDate: Date;
  inProgressDate: Date;
  dropOffArrivedDate: Date;
  cancelledBy: string;
  riderPaymentAccountId: string;
  riderPaymentAccountString: string;
  chatMessages: Array<ChatMessage> = [];
  supportedPaymentMethods: Array<OrganizationPaymentMethod> = [];
  transitProgramInfo: TransitProgramInfo;
  providerFeeSchedule: ProviderFeeScheduleDetail;
  clientFeeSchedule: ClientFeeScheduleDetail;
  rideBackupEligible: boolean;
  lyftAutoDispatchEnabled: boolean;
  minimumServiceLevel: ServiceLevel;
  executedServiceLevel: ServiceLevel;

  // Optional variables used in collecting rider fare.
  fromStatus?: boolean;

  get status(): string {
    // It's a ride request
    if (isNullOrUndefined(this.id)) {
      if (!this.rideRequestDetail.active) {
        return ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_CANCELLED;
      }
      if (this.rideRequestDetail.hasInterestedDrivers) {
        return ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_SELECT_DRIVER;
      }
      if (this.rideRequestDetail.rideRequestType.code === RideRequestTypeConstants.WILL_CALL) {
        return ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_WILL_CALL;
      }
      if (this.rideRequestDetail.rideRequestType.code === RideRequestTypeConstants.RECOVERY) {
        return ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_RECOVERY;
      }
      if (this.rideRequestDetail.rideRequestType.code === RideRequestTypeConstants.RIDE_NOW) {
        return ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_RIDE_NOW;
      }

      return ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_PENDING;
    }
    // If it's a reservation and it's canceled
    if (this.cancelled) {
      return ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_CANCELLED;
    }
    // Otherwise just use the status
    return this.reservationStatus.name;
  }

  get riderFullName(): string {
    return this.rideRequestDetail.organizationRider.rider.firstName + ' ' + this.rideRequestDetail.organizationRider.rider.lastName;
  }

  get driverFullName(): string {
    return !isNullOrUndefined(this.driver) ? this.driver.firstName + ' ' + this.driver.lastName : 'N/A';
  }

  get pickupDate(): Date {
    return this.rideRequestDetail.pickupDate;
  }

  get formattedActivatedAtDate(): string {
    if (isNullOrUndefined(this.rideRequestDetail.activatedAt)) {
      return null;
    }
    return TimeZoneUtil.formatTimeZoneDate(
      this.rideRequestDetail.activatedAt,
      this.rideRequestDetail.timeZone,
      DateFormatConstants.TIME_SHORT_TIMEZONE
    );
  }

  get formattedDriverEnRouteDate(): string {
    if (isNullOrUndefined(this.driverEnRouteDate)) {
      return null;
    }
    return TimeZoneUtil.formatTimeZoneDate(
      this.driverEnRouteDate,
      this.rideRequestDetail.timeZone,
      DateFormatConstants.TIME_SHORT_TIMEZONE
    );
  }

  get formattedPickUpArrivedDate(): string {
    if (isNullOrUndefined(this.pickUpArrivedDate)) {
      return null;
    }
    return TimeZoneUtil.formatTimeZoneDate(
      this.pickUpArrivedDate,
      this.rideRequestDetail.timeZone,
      DateFormatConstants.TIME_SHORT_TIMEZONE
    );
  }

  get formattedInProgressDate(): string {
    if (isNullOrUndefined(this.inProgressDate)) {
      return null;
    }
    return TimeZoneUtil.formatTimeZoneDate(this.inProgressDate, this.rideRequestDetail.timeZone, DateFormatConstants.TIME_SHORT_TIMEZONE);
  }

  get formattedDropOffArrivedDate(): string {
    if (isNullOrUndefined(this.dropOffArrivedDate)) {
      return null;
    }
    return TimeZoneUtil.formatTimeZoneDate(
      this.dropOffArrivedDate,
      this.rideRequestDetail.timeZone,
      DateFormatConstants.TIME_SHORT_TIMEZONE
    );
  }

  get formattedCompletedDate(): string {
    if (isNullOrUndefined(this.completedDate)) {
      return null;
    }
    return TimeZoneUtil.formatTimeZoneDate(this.completedDate, this.rideRequestDetail.timeZone, DateFormatConstants.TIME_SHORT_TIMEZONE);
  }

  get numberOfReadChatMessages(): number {
    if (isNullOrUndefined(this.id)) {
      return 0;
    }

    const reservationReadChatCounts: any = JSON.parse(
      localStorage.getItem(LocalStorageConstants.LOCAL_STORAGE_KEY_RESERVATION_READ_CHAT_COUNTS)
    );

    if (isNullOrUndefined(reservationReadChatCounts)) {
      return 0;
    }

    const count: number = reservationReadChatCounts[this.id.toString()];

    return isNullOrUndefined(count) ? 0 : count;
  }

  set numberOfReadChatMessages(count: number) {
    if (isNullOrUndefined(this.id)) {
      return;
    }

    let reservationReadChatCounts: any = JSON.parse(
      localStorage.getItem(LocalStorageConstants.LOCAL_STORAGE_KEY_RESERVATION_READ_CHAT_COUNTS)
    );

    if (isNullOrUndefined(reservationReadChatCounts)) {
      reservationReadChatCounts = {};
    }

    reservationReadChatCounts[this.id.toString()] = count;

    localStorage.setItem(LocalStorageConstants.LOCAL_STORAGE_KEY_RESERVATION_READ_CHAT_COUNTS, JSON.stringify(reservationReadChatCounts));
  }

  get badgeHtml(): GenericListIconDefinition {
    if (this.hasNewChatMessages) {
      return new GenericListIconDefinition('chat_bubble');
    }

    return null;
  }

  get totalMileage(): number {
    const miles = this.rideRequestDetail.distanceEstimate * 0.000621371;
    return parseFloat(miles.toFixed(1));
  }

  get hasNewChatMessages(): boolean {
    if (isNullOrUndefined(this.id)) {
      return false;
    }

    return this.chatMessages.length > this.numberOfReadChatMessages;
  }

  get isPending(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_PENDING;
  }

  get isRecovery(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_RECOVERY;
  }

  get isWillCall(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_WILL_CALL;
  }

  get isSelectDriver(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_SELECT_DRIVER;
  }

  get isReservation(): boolean {
    return !isNullOrUndefined(this.driver);
  }

  get isScheduled(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_SCHEDULED;
  }

  get isDriverEnRoute(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_EN_ROUTE;
  }

  get isPickUpArrived(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_PICK_UP_ARRIVED;
  }

  get isInProgress(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_IN_PROGRESS;
  }

  get isDropOffArrived(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_DROP_OFF_ARRIVED;
  }

  get isCompleted(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_COMPLETED;
  }

  get isCancelled(): boolean {
    return this.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_CANCELLED;
  }

  get canEdit(): boolean {
    if (
      this.isDriverEnRoute ||
      this.isPickUpArrived ||
      this.isInProgress ||
      this.isDropOffArrived ||
      this.isCancelled ||
      this.isCompleted
    ) {
      return false;
    }
    return true;
  }

  get canChat(): boolean {
    return !isNullOrUndefined(this.driver) && !this.isCancelled && !this.isCompleted;
  }

  get canCancel(): boolean {
    return !this.isCancelled && !this.isCompleted;
  }

  static reservationListItemVmFromReservationDetail(reservationDetail: ReservationDetail): ReservationListItemVm {
    const reservationVm: ReservationListItemVm = new ReservationListItemVm();

    reservationVm.id = reservationDetail.id;
    reservationVm.driver = reservationDetail.driver;
    reservationVm.provider = reservationDetail.provider;
    reservationVm.vehicle = reservationDetail.vehicle;
    reservationVm.vehicleId = reservationDetail.vehicleId;
    reservationVm.rideRequestDetail = reservationDetail.rideRequestDetail;
    reservationVm.reservationStatus = reservationDetail.reservationStatus;
    reservationVm.cancelled = reservationDetail.cancelled;
    reservationVm.completedDate = reservationDetail.completedDate;
    reservationVm.createdDate = reservationDetail.rideRequestDetail.createdDate;
    reservationVm.reservedDate = reservationDetail.createdDate;
    reservationVm.modifiedDate = reservationDetail.modifiedDate;
    reservationVm.chatMessages = reservationDetail.chatMessages;
    reservationVm.cancellationReason = reservationDetail.cancellationReason;
    reservationVm.cancelledDate = reservationDetail.cancelledDate;
    reservationVm.cancelledBy = reservationDetail.cancelledBy;
    reservationVm.reservedBy = reservationDetail.createdBy;
    reservationVm.createdBy = reservationDetail.rideRequestDetail.createdBy;
    reservationVm.driverEnRouteDate = reservationDetail.driverEnRouteDate;
    reservationVm.pickUpArrivedDate = reservationDetail.pickUpArrivedDate;
    reservationVm.inProgressDate = reservationDetail.inProgressDate;
    reservationVm.dropOffArrivedDate = reservationDetail.dropOffArrivedDate;
    reservationVm.transitProgramInfo = reservationDetail.rideRequestDetail.transitProgramInfo;
    reservationVm.providerFeeSchedule = reservationDetail.providerFeeScheduleDetail;
    reservationVm.clientFeeSchedule = reservationDetail.clientFeeScheduleDetail;
    reservationVm.supportedPaymentMethods = reservationDetail.supportedPaymentMethods;
    reservationVm.minimumServiceLevel = reservationDetail.rideRequestDetail.serviceLevel;
    reservationVm.executedServiceLevel = reservationDetail.serviceLevel;
    return reservationVm;
  }

  static reservationListItemVmFromRideRequestDetail(rideRequest: RideRequestDetail): ReservationListItemVm {
    const reservationVm: ReservationListItemVm = new ReservationListItemVm();

    reservationVm.rideRequestDetail = rideRequest;
    reservationVm.createdBy = rideRequest.createdBy;
    reservationVm.createdDate = rideRequest.createdDate;
    reservationVm.modifiedDate = rideRequest.modifiedDate;
    reservationVm.cancellationReason = rideRequest.cancellationReason;
    reservationVm.cancelledDate = rideRequest.cancelledDate;
    reservationVm.cancelledBy = rideRequest.cancelledBy;
    reservationVm.transitProgramInfo = rideRequest.transitProgramInfo;
    reservationVm.lyftAutoDispatchEnabled = rideRequest.transitProgramInfo.lyftAutoDispatchEnabled;
    reservationVm.minimumServiceLevel = rideRequest.serviceLevel;
    reservationVm.rideBackupEligible = rideRequest.rideBackupEligible;

    return reservationVm;
  }
}
